import { useTheme } from "../../../contexts/ThemeContext";

const steps = [
  {
    number: "01", icon: "ri-search-eye-line", nodeColor: "#38BDF8", title: "Assess", subtitle: "We map your revenue gaps",
    desc: "We analyze your current lead flow, call volume, response times, and booking process to identify exactly where revenue is leaking.",
    points: ["Lead flow audit", "Response time analysis", "Gap identification", "Custom deployment plan"],
  },
  {
    number: "02", icon: "ri-rocket-2-line", nodeColor: "#F97316", title: "Deploy", subtitle: "Your AI workforce goes live",
    desc: "We configure your full AI workforce — tuned to your brand, voice, and workflows. Integrates seamlessly with your existing tools in days.",
    points: ["AI receptionist setup", "CRM & calendar sync", "Brand voice configuration", "Workflow automation"],
  },
  {
    number: "03", icon: "ri-line-chart-line", nodeColor: "#22C55E", title: "Optimize", subtitle: "Performance scales with you",
    desc: "We monitor results continuously, refine every touchpoint, and improve conversion rates. Your AI workforce gets smarter as you grow.",
    points: ["Weekly performance reviews", "Conversion optimization", "Response refinement", "Ongoing system updates"],
  },
];

export default function DeploymentProcess() {
  const { isDark } = useTheme();

  const sectionBg = isDark ? "bg-[#050508]" : "bg-[#F5F5F7]";
  const titleColor = isDark ? "text-white" : "text-[#1d1d1f]";
  const subColor = isDark ? "text-white/45" : "text-[#6e6e73]";
  const nodeBg = isDark ? "bg-[#0c0c14]" : "bg-white";
  const nodeNumColor = isDark ? "text-white/20 group-hover:text-white/40" : "text-black/15 group-hover:text-black/30";
  const cardBg = isDark ? "bg-[#0c0c14] border-white/[0.06] group-hover:border-white/12" : "bg-white border-black/[0.07] group-hover:border-black/12";
  const cardPointColor = isDark ? "text-white/50" : "text-[#6e6e73]";

  return (
    <section id="deployment" className={`${sectionBg} py-24 md:py-36 px-6 md:px-10 transition-colors duration-300`}>
      <div className="max-w-[1280px] mx-auto">

        <div className="text-center mb-20">
          <p className="text-[#F97316] text-[11px] font-semibold tracking-[0.2em] uppercase mb-5">Deployment Process</p>
          <h2 className={`text-[42px] md:text-[60px] font-black ${titleColor} tracking-[-0.03em] leading-tight mb-5`}>
            From conversation<br />to live in days.
          </h2>
          <p className={`${subColor} text-[17px] max-w-md mx-auto leading-relaxed`}>
            A straightforward three-phase process that gets your AI workforce operational fast — zero disruption to your business.
          </p>
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="hidden lg:block absolute top-[52px] left-[calc(16.67%+52px)] right-[calc(16.67%+52px)] h-[1px] bg-gradient-to-r from-[#38BDF8]/30 via-[#F97316]/40 to-[#22C55E]/30 pointer-events-none"></div>

          {steps.map((step, i) => (
            <div key={i} className="group flex flex-col items-center text-center">
              <div className={`relative w-[104px] h-[104px] flex items-center justify-center rounded-full border ${nodeBg} mb-8 transition-all duration-300`} style={{ borderColor: `${step.nodeColor}30` }}>
                <span className={`text-[32px] font-black transition-colors duration-300 ${nodeNumColor}`}>{step.number}</span>
                <div className="absolute bottom-[-4px] right-[-4px] w-9 h-9 flex items-center justify-center rounded-full" style={{ backgroundColor: step.nodeColor }}>
                  <i className={`${step.icon} text-black text-sm`}></i>
                </div>
              </div>
              <div className={`w-full border rounded-[24px] p-7 transition-all duration-300 text-left ${cardBg}`}>
                <p className="text-[11px] font-semibold tracking-widest uppercase mb-2" style={{ color: step.nodeColor }}>{step.subtitle}</p>
                <h3 className={`${titleColor} font-black text-[28px] tracking-tight mb-3`}>{step.title}</h3>
                <p className={`${subColor} text-[14px] leading-relaxed mb-5`}>{step.desc}</p>
                <ul className="flex flex-col gap-2.5">
                  {step.points.map((pt, j) => (
                    <li key={j} className={`flex items-center gap-2.5 ${cardPointColor} text-[13px]`}>
                      <i className="ri-check-line shrink-0" style={{ color: step.nodeColor }}></i>
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Banner — always cinematic */}
        <div className="mt-12 rounded-[28px] overflow-hidden relative">
          <img
            src="https://public.readdy.ai/ai/img_res/55c988896c4329baf34a706baa176172.jpg"
            alt="AI Operations"
            className="w-full h-[320px] md:h-[400px] object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/55 to-transparent"></div>
          <div className="absolute inset-0 flex flex-col justify-center px-10 md:px-16">
            <h3 className="text-white font-black text-[32px] md:text-[44px] tracking-tight leading-tight mb-4 max-w-md">
              Ready to deploy your AI workforce?
            </h3>
            <p className="text-white/50 text-[15px] mb-8 max-w-sm">Most clients are fully operational within days of their first assessment call.</p>
            <button
              onClick={() => { const el = document.getElementById("contact"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
              className="inline-flex items-center gap-2 bg-[#F97316] text-white text-[14px] font-bold px-7 py-3 rounded-full hover:bg-[#FB923C] hover:scale-[1.02] transition-all duration-200 cursor-pointer whitespace-nowrap w-fit"
            >
              Schedule Your Assessment <i className="ri-arrow-right-line text-sm"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
